import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { X, RotateCcw, Play, Pause, Flame, ZoomIn, ZoomOut, Maximize, MapPin } from 'lucide-react';

/**
 * Immersive draggable 360° sphere viewer (equirectangular). Reused across Object Intelligence
 * (transient `detections`) and Phase 7A Site Walks (persistent `hotspots` records). Adds zoom,
 * fullscreen and an add-hotspot mode while preserving the original detection behaviour.
 *
 * Coordinate contract: hotspot coords are PANORAMA-relative {yaw,pitch} in RADIANS — viewer
 * orientation only, never world/BIM coordinates.
 */
const R = 500;

const uvToDir = (uNorm, vNorm) => {
  const lon = (uNorm - 0.5) * 2 * Math.PI;
  const lat = (0.5 - vNorm) * Math.PI;
  const phi = Math.PI / 2 - lat;
  return new THREE.Vector3(Math.sin(phi) * Math.cos(lon), Math.cos(phi), Math.sin(phi) * Math.sin(lon));
};
const yawPitchToDir = (yaw, pitch) => {
  const phi = Math.PI / 2 - pitch;
  return new THREE.Vector3(Math.sin(phi) * Math.cos(yaw), Math.cos(phi), Math.sin(phi) * Math.sin(yaw));
};
const dirToYawPitch = (v) => {
  const n = v.clone().normalize();
  return { yaw: Math.atan2(n.z, n.x), pitch: Math.asin(Math.max(-1, Math.min(1, n.y))) };
};

const HOTSPOT_COLORS = {
  NAVIGATION: '#3b82f6', NOTE: '#64748b', ISSUE: '#ef4444', QUALITY: '#8b5cf6',
  SAFETY: '#f59e0b', PROGRESS: '#10b981', DRAWING_BIM: '#0ea5e9',
};

const Sphere360Viewer = ({
  imageUrl, detections = [], onSelectDetection,
  hotspots = [], onAddHotspot, onSelectHotspot, addMode = false,
  onView, viewOverride,
}) => {
  const mountRef = useRef(null);
  const stateRef = useRef({ lon: 0, lat: 0, dragging: false, px: 0, py: 0, autoSpin: true, showHeat: true, fov: 72, addMode });
  const camRef = useRef(null);
  const [selected, setSelected] = useState(null);
  const [spinning, setSpinning] = useState(true);
  const [heatOn, setHeatOn] = useState(true);

  const damagedCount = detections.filter((d) => d.quality && d.quality.status === 'damaged').length;
  stateRef.current.addMode = addMode;

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || !imageUrl) return;
    const width = mount.clientWidth || 640;
    const height = mount.clientHeight || 360;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(stateRef.current.fov, width / height, 1, 1100);
    camera.position.set(0, 0, 0);
    camRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    renderer.setSize(width, height);
    mount.appendChild(renderer.domElement);

    const geometry = new THREE.SphereGeometry(R, 60, 40);
    geometry.scale(-1, 1, 1);
    const texture = new THREE.TextureLoader().load(imageUrl, () => renderer.render(scene, camera));
    texture.colorSpace = THREE.SRGBColorSpace;
    const sphereMesh = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ map: texture }));
    scene.add(sphereMesh);

    const makeDot = (color) => {
      const c = document.createElement('canvas'); c.width = c.height = 64;
      const ctx = c.getContext('2d');
      ctx.beginPath(); ctx.arc(32, 32, 22, 0, Math.PI * 2); ctx.fillStyle = color; ctx.fill();
      ctx.lineWidth = 6; ctx.strokeStyle = '#ffffff'; ctx.stroke();
      const tex = new THREE.CanvasTexture(c); tex.colorSpace = THREE.SRGBColorSpace; return tex;
    };
    const makeGlow = () => {
      const c = document.createElement('canvas'); c.width = c.height = 128;
      const ctx = c.getContext('2d');
      const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
      g.addColorStop(0, 'rgba(239,68,68,0.9)'); g.addColorStop(1, 'rgba(239,68,68,0)');
      ctx.fillStyle = g; ctx.fillRect(0, 0, 128, 128);
      const tex = new THREE.CanvasTexture(c); tex.colorSpace = THREE.SRGBColorSpace; return tex;
    };

    const pickables = [];
    const heatGroup = new THREE.Group();
    scene.add(heatGroup);
    const glowTex = makeGlow();

    // Transient detections (Object Intelligence)
    detections.forEach((det, idx) => {
      const e = det.equirect || {};
      if (typeof e.uNorm !== 'number') return;
      const damaged = det.quality && det.quality.status === 'damaged';
      const dir = uvToDir(e.uNorm, e.vNorm);
      const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: makeDot(damaged ? '#ef4444' : '#10b981'), depthTest: false }));
      sprite.position.copy(dir.clone().multiplyScalar(R * 0.94));
      sprite.scale.set(34, 34, 1);
      sprite.userData = { kind: 'detection', det, idx };
      scene.add(sprite); pickables.push(sprite);
      if (damaged) {
        const glow = new THREE.Sprite(new THREE.SpriteMaterial({ map: glowTex, blending: THREE.AdditiveBlending, depthTest: false, transparent: true, opacity: 0.9 }));
        glow.position.copy(dir.clone().multiplyScalar(R * 0.9)); glow.scale.set(150, 150, 1);
        heatGroup.add(glow);
      }
    });

    // Persistent Site Walk hotspot records
    (hotspots || []).forEach((hs) => {
      const c = hs.coord || {};
      if (typeof c.yaw !== 'number' || typeof c.pitch !== 'number') return;
      const dir = yawPitchToDir(c.yaw, c.pitch);
      const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: makeDot(HOTSPOT_COLORS[hs.type] || '#64748b'), depthTest: false }));
      sprite.position.copy(dir.clone().multiplyScalar(R * 0.9));
      sprite.scale.set(38, 38, 1);
      sprite.userData = { kind: 'hotspot', hs };
      scene.add(sprite); pickables.push(sprite);
    });

    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    const st = stateRef.current;

    const onDown = (ev) => {
      st.dragging = true; st.autoSpin = false; setSpinning(false);
      const p = ev.touches ? ev.touches[0] : ev;
      st.px = p.clientX; st.py = p.clientY; st.moved = false;
    };
    const onMove = (ev) => {
      if (!st.dragging) return;
      const p = ev.touches ? ev.touches[0] : ev;
      const dx = p.clientX - st.px; const dy = p.clientY - st.py;
      st.px = p.clientX; st.py = p.clientY;
      if (Math.abs(dx) + Math.abs(dy) > 3) st.moved = true;
      const k = st.fov / 72;
      st.lon -= dx * 0.18 * k;
      st.lat = Math.max(-85, Math.min(85, st.lat + dy * 0.18 * k));
      onView && onView({ lon: st.lon, lat: st.lat, fov: st.fov });
    };
    const onUp = (ev) => {
      st.dragging = false;
      if (st.moved) return;
      const rect = renderer.domElement.getBoundingClientRect();
      const p = (ev.changedTouches ? ev.changedTouches[0] : ev);
      pointer.x = ((p.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((p.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointer, camera);
      // Add-hotspot mode: intersect the sphere and emit yaw/pitch
      if (st.addMode && onAddHotspot) {
        const hit = raycaster.intersectObject(sphereMesh, false);
        if (hit.length > 0) { onAddHotspot(dirToYawPitch(hit[0].point)); return; }
      }
      const hits = raycaster.intersectObjects(pickables, false);
      if (hits.length > 0) {
        const ud = hits[0].object.userData;
        if (ud.kind === 'detection') { setSelected({ det: ud.det, idx: ud.idx }); onSelectDetection && onSelectDetection(ud.det, ud.idx); }
        else if (ud.kind === 'hotspot') { setSelected({ hs: ud.hs }); onSelectHotspot && onSelectHotspot(ud.hs); }
      }
    };
    const onWheel = (ev) => {
      ev.preventDefault();
      st.fov = Math.max(30, Math.min(100, st.fov + (ev.deltaY > 0 ? 4 : -4)));
      camera.fov = st.fov; camera.updateProjectionMatrix();
      onView && onView({ lon: st.lon, lat: st.lat, fov: st.fov });
    };

    const el = renderer.domElement;
    el.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    el.addEventListener('touchstart', onDown, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: true });
    el.addEventListener('touchend', onUp);
    el.addEventListener('wheel', onWheel, { passive: false });

    let raf;
    const target = new THREE.Vector3();
    const animate = () => {
      raf = requestAnimationFrame(animate);
      if (st.autoSpin && !st.dragging && !st.addMode) st.lon -= 0.08;
      heatGroup.visible = st.showHeat;
      const lonR = THREE.MathUtils.degToRad(st.lon);
      const latR = THREE.MathUtils.degToRad(st.lat);
      const phi = Math.PI / 2 - latR;
      target.set(Math.sin(phi) * Math.cos(lonR), Math.cos(phi), Math.sin(phi) * Math.sin(lonR));
      camera.fov = st.fov; camera.updateProjectionMatrix();
      camera.lookAt(target);
      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      const w = mount.clientWidth || width; const h = mount.clientHeight || height;
      camera.aspect = w / h; camera.updateProjectionMatrix(); renderer.setSize(w, h);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      el.removeEventListener('mousedown', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      el.removeEventListener('touchstart', onDown);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onUp);
      el.removeEventListener('wheel', onWheel);
      geometry.dispose(); texture.dispose(); glowTex.dispose();
      pickables.forEach((s) => { s.material.map && s.material.map.dispose(); s.material.dispose(); });
      heatGroup.children.forEach((g) => g.material.dispose());
      renderer.dispose();
      if (el.parentNode) el.parentNode.removeChild(el);
    };
  }, [imageUrl, detections, hotspots, onSelectDetection, onAddHotspot, onSelectHotspot]);

  // Synchronized VIEW ORIENTATION from the paired viewer (not geometric registration).
  useEffect(() => {
    if (!viewOverride) return;
    const st = stateRef.current;
    st.lon = viewOverride.lon; st.lat = viewOverride.lat; st.fov = viewOverride.fov; st.autoSpin = false;
    setSpinning(false);
  }, [viewOverride]);

  const toggleSpin = () => { stateRef.current.autoSpin = !stateRef.current.autoSpin; setSpinning(stateRef.current.autoSpin); };  const resetView = () => { stateRef.current.lon = 0; stateRef.current.lat = 0; stateRef.current.fov = 72; stateRef.current.autoSpin = true; setSpinning(true); };
  const toggleHeat = () => { stateRef.current.showHeat = !stateRef.current.showHeat; setHeatOn(stateRef.current.showHeat); };
  const zoom = (d) => { stateRef.current.fov = Math.max(30, Math.min(100, stateRef.current.fov + d)); };
  const fullscreen = () => { const n = mountRef.current; if (n && n.requestFullscreen) n.requestFullscreen(); };

  const btn = 'flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-black/45 text-white hover:bg-black/60';

  return (
    <div className="relative w-full">
      <div
        ref={mountRef}
        className={`w-full h-96 rounded-lg overflow-hidden bg-slate-900 select-none ${addMode ? 'cursor-crosshair' : 'cursor-grab active:cursor-grabbing'}`}
        data-testid="sphere-360-viewer"
      />
      <div className="absolute top-2 left-2 flex flex-wrap gap-1.5 z-10">
        <button onClick={toggleSpin} className={btn} data-testid="sphere-spin-toggle">
          {spinning ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}{spinning ? 'Pause' : 'Spin'}
        </button>
        <button onClick={resetView} className={btn} data-testid="sphere-reset-view"><RotateCcw className="w-3 h-3" /> Reset</button>
        <button onClick={() => zoom(-8)} className={btn} data-testid="sphere-zoom-in"><ZoomIn className="w-3 h-3" /></button>
        <button onClick={() => zoom(8)} className={btn} data-testid="sphere-zoom-out"><ZoomOut className="w-3 h-3" /></button>
        <button onClick={fullscreen} className={btn} data-testid="sphere-fullscreen"><Maximize className="w-3 h-3" /></button>
        {damagedCount > 0 && (
          <button onClick={toggleHeat} className={`${btn} ${heatOn ? 'ring-1 ring-red-400' : ''}`} data-testid="sphere-heat-toggle"><Flame className="w-3 h-3" /> Damage heat</button>
        )}
      </div>
      <span className="absolute bottom-2 left-2 text-[11px] text-white/80 bg-black/40 rounded px-2 py-0.5 pointer-events-none">
        {addMode ? 'Click on the panorama to place a hotspot' : `Drag to look around · scroll to zoom${(hotspots.length || detections.length) ? ' · tap a pin' : ''}`}
      </span>
      {addMode && (
        <span className="absolute top-2 right-2 z-10 text-[11px] bg-blue-600 text-white rounded px-2 py-1 flex items-center gap-1" data-testid="sphere-add-mode-badge">
          <MapPin className="w-3 h-3" /> Placing hotspot…
        </span>
      )}
      {selected && selected.det && (
        <div className="absolute top-2 right-2 w-44 bg-white rounded-lg shadow-xl border border-slate-200 p-2 z-10" data-testid="sphere-hotspot-popover">
          <div className="flex justify-between items-start">
            <p className="text-xs font-semibold text-slate-800">{selected.det.id || `Object ${selected.idx + 1}`}</p>
            <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-600"><X className="w-3 h-3" /></button>
          </div>
          {selected.det.cluster && <p className="text-[11px] text-slate-500">Cluster {selected.det.cluster}</p>}
          {selected.det.quality && (
            <p className={`text-[11px] font-medium ${selected.det.quality.status === 'damaged' ? 'text-red-600' : 'text-emerald-600'}`}>
              {selected.det.quality.status === 'damaged' ? 'Damaged' : 'OK'}
              {selected.det.quality.notes?.length ? ` · ${selected.det.quality.notes[0]}` : ''}
            </p>
          )}
        </div>
      )}
      {selected && selected.hs && (
        <div className="absolute top-2 right-2 w-48 bg-white rounded-lg shadow-xl border border-slate-200 p-2 z-10" data-testid="sphere-hotspot-record-popover">
          <div className="flex justify-between items-start">
            <p className="text-xs font-semibold text-slate-800">{selected.hs.label || selected.hs.type}</p>
            <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-600"><X className="w-3 h-3" /></button>
          </div>
          <p className="text-[11px] mt-0.5" style={{ color: HOTSPOT_COLORS[selected.hs.type] || '#64748b' }}>{selected.hs.type}</p>
          {selected.hs.note && <p className="text-[11px] text-slate-500 mt-1">{selected.hs.note}</p>}
        </div>
      )}
    </div>
  );
};

export default Sphere360Viewer;
